import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Checklist, Drawer, Field, FieldGroup, Input, Select } from '@itds/elements/index.js';
import { IUL_FEATURES, PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE } from '@itds/Constants';
import { CarrierContext } from '@carrier/contexts/CarrierContext';
import { ACTION_MESSAGES } from '@carrier/utils/custom_hooks/useNotification';

import Api from '@carrier/utils/Api';

const DEATH_BENEFIT_TYPES = {
  LEVEL: 'Level',
  INCREASING: 'Increasing'
};

const generateInitialState = (product = {}) => ({
  agent_message: product?.agent_message || '',
  applicint_id: product?.applicint_id || '',
  applicint_name: product?.applicint_name || '',
  carrier_api_id: product?.carrier_api_id || '',
  carrier_id: product?.carrier_id || '',
  cusip: product?.cusip || '',
  eticket_code: product?.eticket_code || '',
  eticket_name: product?.eticket_name || '',
  id: product?.id || '',
  line_of_coverage: 'Annuity',
  product_feature_ids: product?.product_feature_ids || [],
  product_group_id: product?.product_group_id || '',
  product_name: product?.product_name || '',
  product_type_detail: product?.product_type_detail || '',
  product_type: product?.product_type || '',
  psg_id: product?.psg_id || '',
  death_benefit_types: product?.death_benefit_types || ['Level', 'Increasing'] // UPDATED: Changed from death_benefits to death_benefit_types
});

const EditProduct = ({ product, productGroups, onClose, onDelete, onSave, show }) => {
  const { setNotification } = useContext(CarrierContext);

  const [data, setData] = useState(generateInitialState(product));
  const [show_delete_warning, setDeleteWarning] = useState(false);
  const [product_features, setProductFeatures] = useState([]);
  const [product_features_iul, setProductFeaturesIul] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setData(generateInitialState(product));
  }, [product]);

  useEffect(() => {
    Api.loadProductFeatures().then(({ data }) => {
      setProductFeatures(data);
    });
  }, []);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
    if (name === 'product_type') {
      if (e.target.value === 'IUL') {
        // Load and filter product features for IUL
        const iulFeatures = product_features.filter(feature =>
          IUL_FEATURES.includes(feature.name)
        );

        setProductFeaturesIul(iulFeatures);

        // Set default death benefit types for IUL if not already set
        if (!data.death_benefit_types || data.death_benefit_types.length === 0) {
          setData(prevData => ({
            ...prevData,
            product_type: e.target.value,
            death_benefit_types: ['Level', 'Increasing']
          }));
        }
      } else {
        // Clear death benefit types if not IUL
        setData(prevData => ({
          ...prevData,
          product_type: e.target.value,
          death_benefit_types: []
        }));
      }
    }
  };

  const handleFeaturesChange = product_feature_ids => {
    setData({ ...data, product_feature_ids });
  };

  // UPDATED: Handle death benefit types checkbox changes
  const handleDeathBenefitChange = (benefitType, e) => {
    const isChecked = e.target.checked;
    let newDeathBenefitTypes = [...data.death_benefit_types];

    if (isChecked) {
      // Add benefit if not already present
      if (!newDeathBenefitTypes.includes(benefitType)) {
        newDeathBenefitTypes.push(benefitType);
      }
    } else {
      // Remove benefit only if at least one other benefit is selected
      if (newDeathBenefitTypes.length > 1) {
        newDeathBenefitTypes = newDeathBenefitTypes.filter(b => b !== benefitType);
      }
      // If trying to uncheck the last one, prevent it by not updating state
      else {
        return; // Do nothing - at least one must remain checked
      }
    }

    setData({ ...data, death_benefit_types: newDeathBenefitTypes });
  };

  const handleDelete = () => {
    Api.deleteProduct(data.id)
      .then(response => {
        onDelete(response.data);
        setNotification('');
        window.scrollTo(0, 0);
        setNotification(ACTION_MESSAGES.CARRIERS.PRODUCT.DELETE);
      })
      .catch(() => {
        onSave(data);
        setNotification('');
        window.scrollTo(0, 0);
        setNotification(ACTION_MESSAGES.CARRIERS.PRODUCT.DELETE_ERROR);
      });
  };

  const handleSave = () => {
    const errors = [];

    if (!data.product_name || data.product_name.length < 3) {
      errors.push('product_name');
    }

    if (!data.product_group_id) {
      errors.push('product_group_id');
    }

    if (!data.product_type) {
      errors.push('product_type');
    }

    if (!data.product_type_detail) {
      errors.push('product_type_detail');
    }

    // UPDATED: Validate death benefit types for IUL
    if (data.product_type === 'IUL' && (!data.death_benefit_types || data.death_benefit_types.length === 0)) {
      errors.push('death_benefit_types');
    }

    setErrors(errors);

    if (!errors.length) {
      const { id, ...payload } = data;

      // UPDATED: Ensure death_benefit_types is sent as array for IUL, empty array for others
      if (payload.product_type !== 'IUL') {
        payload.death_benefit_types = [];
      }

      Api.saveProduct(id, payload)
        .then(({ data }) => {
          onSave(data);
          setNotification('');
          window.scrollTo(0, 0);
          setNotification(ACTION_MESSAGES.CARRIERS.PRODUCT.SAVE);
        })
        .catch(() => {
          onSave(data);
          setNotification('');
          window.scrollTo(0, 0);
          setNotification(ACTION_MESSAGES.CARRIERS.PRODUCT.SAVE_ERROR);
        });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        show_delete_warning ? (
          <>
            <Button color='RED' onClick={handleDelete}>
              Confirm Deletion
            </Button>
            <Button
              onClick={setDeleteWarning.bind(null, false)}
              outline={true}
              style={{ marginLeft: 10 }}
            >
              Nevermind, I made a mistake
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleSave}>{data.id ? 'Save Changes' : 'Create Product'}</Button>
            {data.id ? (
              <Button
                color='RED'
                onClick={setDeleteWarning.bind(null, true)}
                outline={true}
                style={{ marginLeft: 10 }}
              >
                Delete
              </Button>
            ) : null}
          </>
        )
      }
      onClose={onClose}
      show={show}
      title={show_delete_warning ? 'Caution!' : `${data.id ? 'Edit' : 'New'} Product`}
    >
      {show_delete_warning ? (
        <div style={{ fontSize: 16, lineHeight: '1.6em', textAlign: 'center' }}>
          Deleting this product will delete it's quote and form data! This means, they will no
          longer be available to partners. Proceed with caution.
        </div>
      ) : (
        <>
          <Field label='Name'>
            <Input
              invalid={errors.includes('product_name')}
              onChange={handleInputChange.bind(null, 'product_name')}
              required={true}
              value={data.product_name}
            />
          </Field>

          <Field label='Product Group'>
            <Select
              invalid={errors.includes('product_group_id')}
              onChange={handleInputChange.bind(null, 'product_group_id')}
              options={[{ value: '', label: '-- Select One --' }].concat(
                productGroups.map(pg => ({ value: pg.id, label: pg.group_name }))
              )}
              required={true}
              value={data.product_group_id}
            />
          </Field>

          <Field label='Type'>
            <Select
              invalid={errors.includes('product_type')}
              onChange={handleInputChange.bind(null, 'product_type')}
              options={[{ value: '', label: '-- Select One --' }].concat(
                Object.keys(PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE).map(value => ({ value }))
              )}
              required={true}
              value={data.product_type}
            />
          </Field>

          {data.product_type ? (
            <Field label='Detail'>
              <Select
                invalid={errors.includes('product_type_detail')}
                onChange={handleInputChange.bind(null, 'product_type_detail')}
                options={[{ value: '', label: '-- Select One --' }].concat(
                  PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE[data?.product_type]?.map(value => ({
                    value
                  }))
                )}
                required={true}
                value={data?.product_type_detail}
              />
            </Field>
          ) : null}

          {/* UPDATED: Death Benefits Section for IUL */}
          {data.product_type === 'IUL' ? (
            <FieldGroup
              contained={true}
              description='At least one death benefit type must be selected'
              label='Death Benefits'
              required={true}
            >
              <div style={{ marginBottom: '10px' }}>
                <Checkbox
                  checked={data.death_benefit_types?.includes(DEATH_BENEFIT_TYPES.LEVEL)}
                  label='Level Death Benefit'
                  onChange={handleDeathBenefitChange.bind(null, DEATH_BENEFIT_TYPES.LEVEL)}
                />
              </div>
              <div>
                <Checkbox
                  checked={data.death_benefit_types?.includes(DEATH_BENEFIT_TYPES.INCREASING)}
                  label='Increasing Death Benefit'
                  onChange={handleDeathBenefitChange.bind(null, DEATH_BENEFIT_TYPES.INCREASING)}
                />
              </div>
              {errors.includes('death_benefit_types') && (
                <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '8px' }}>
                  At least one death benefit must be selected
                </div>
              )}
            </FieldGroup>
          ) : null}

          <Field label='Features'>
            <Checklist
              onChange={handleFeaturesChange}
              options={
                data.product_type === 'IUL' ?
                  product_features_iul?.map(pf => ({ value: pf.id, label: pf.name })) :
                  product_features?.map(pf => ({ value: pf.id, label: pf.name }))
              }
              selected={data?.product_feature_ids || []}
            />
          </Field>

          <Field label='Message to the Agent'>
            <Input
              height={200}
              onChange={handleInputChange.bind(null, 'agent_message')}
              type='textarea'
              value={data.agent_message}
            />
          </Field>

          <Field label='Carrier API ID'>
            <Input
              invalid={errors.includes('carrier_api_id')}
              onChange={handleInputChange.bind(null, 'carrier_api_id')}
              value={data.carrier_api_id}
            />
          </Field>

          <Field label='Carrier API E-Ticket Name'>
            <Input
              invalid={errors.includes('eticket_name')}
              onChange={handleInputChange.bind(null, 'eticket_name')}
              value={data.eticket_name}
            />
          </Field>

          <Field label='Carrier API E-Ticket Code'>
            <Input
              invalid={errors.includes('eticket_code')}
              onChange={handleInputChange.bind(null, 'eticket_code')}
              value={data.eticket_code}
            />
          </Field>

          <Field label='Paperless Solutions Group ID'>
            <Input
              invalid={errors.includes('psg_id')}
              onChange={handleInputChange.bind(null, 'psg_id')}
              value={data.psg_id}
            />
          </Field>

          <Field label='Applicint Product Code'>
            <Input
              invalid={errors.includes('applicint_id')}
              onChange={handleInputChange.bind(null, 'applicint_id')}
              value={data.applicint_id}
            />
          </Field>

          <Field label='Applicint Product Name'>
            <Input
              invalid={errors.includes('applicint_name')}
              onChange={handleInputChange.bind(null, 'applicint_name')}
              value={data.applicint_name}
            />
          </Field>

          <Field label='Cusip'>
            <Input
              invalid={errors.includes('cusip')}
              onChange={handleInputChange.bind(null, 'cusip')}
              value={data.cusip}
            />
          </Field>
        </>
      )}
    </Drawer>
  );
};

EditProduct.propTypes = {
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  product: PropTypes.object,
  productGroups: PropTypes.array,
  show: PropTypes.bool
};

export default EditProduct;